import React, { useState, useEffect,useContext} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TableContext } from '../context';
import axios from 'axios';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Divider,
    Alert,
    Button,
    IconButton,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    AutoAwesome as AutoAwesomeIcon,
    ArrowBack as ArrowBackIcon,
    OpenInNew as OpenInNewIcon,
    Assessment as AssessmentIcon,
    Settings as SettingsIcon,
    History as HistoryIcon,
    PlaylistAdd as PlaylistAddIcon
} from '@mui/icons-material';
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McLinkButton } from "@maersk-global/mds-react-wrapper/components-core/mc-link-button";
import { McTag } from "@maersk-global/mds-react-wrapper/components-core/mc-tag";
import { McTabBar } from "@maersk-global/mds-react-wrapper/components-core/mc-tab-bar";
import { McTab } from "@maersk-global/mds-react-wrapper/components-core/mc-tab";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { toast } from 'react-toastify';
import CiInfoDrawer from "./CiInfoDrawer";
import { 
    getUserLists, 
    validateApplications,
    addAppsToList
} from './SubscriptionManagement/apihelpers';
import AddToSubscriptionListModal from './SubscriptionManagement/AddToSubscriptionListModal';

// Import new tab components
import OverviewTab from './ChangeDetailsComponents/OverviewTab';
import AIAnalysisTab from './ChangeDetailsComponents/AIAnalysisTab';
import DependenciesTab from './ChangeDetailsComponents/DependenciesTab';

const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

const ChangeDetailsPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const changeNumber = searchParams.get('changeNumber');
    
    const [changeData, setChangeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState('overview');
    
    // Drawer state for CI info
    const [isdrawerOpen, setisdrawerOpen] = useState(false);
    const [drawerCi, setDrawerCi] = useState("");
    const [drawerCiCategory, setDrawerCiCategory] = useState("");
    const [drawerType, setDrawerType] = useState("");
    const {tokenFetched, graphData} = useContext(TableContext);

    // Add to list modal state
    const [addToListModalOpen, setAddToListModalOpen] = useState(false);

    useEffect(() => {
        if (tokenFetched && changeNumber) {
            fetchChangeDetails();
        }
    }, [tokenFetched, changeNumber]);

    const fetchChangeDetails = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/change_data/getChangeDetails`,
                {
                    change_id: changeNumber,
                    app_name: "",
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                }
            );
            
            if (response.data?.status === 200) {
                setChangeData(response.data);
            } else {
                toast.error("Failed to fetch change details", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
        } catch (error) {
            console.error("Error fetching change details:", error);
            toast.error("Error fetching change details", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
        } finally {
            setLoading(false);
        }
    };

    const getStateAppearance = (status) => {
        switch (status?.toLowerCase()) {
            case 'new':
                return 'neutral';
            case 'assess':
                return 'warning';
            case 'authorize':
                return 'warning';
            case 'scheduled':
                return 'info';
            case 'implement':
                return 'info';
            case 'review':
                return 'success';
            case 'closed':
                return 'success';
            case 'canceled':
                return 'error';
            default:
                return 'neutral';
        }
    };

    const getRiskAppearance = (risk) => {
        switch (risk?.toLowerCase()) {
            case 'low':
                return 'success';
            case 'moderate':
                return 'warning';
            case 'high':
                return 'error';
            default:
                return 'neutral';
        }
    };

    const getTypeAppearance = (type) => {
        switch (type?.toLowerCase()) {
            case 'standard':
                return 'success';
            case 'normal':
                return 'neutral';
            case 'ci/cd':
                return 'warning';
            default:
                return 'neutral';
        }
    };

    const getCategoryAppearance = (category) => {
        switch (category?.toLowerCase()) {
            case 'application':
                return 'success';
            case 'infrastructure':
                return 'info';
            default:
                return 'neutral';
        }
    };

    // Extract all unique CI names from change data
    const getAllCINames = () => {
        if (!changeData) return [];
        
        const ciNames = [];
        const details = changeData.change_details;
        
        // Add main CI first
        const mainCI = details?.cmdb_ci;
        if (mainCI) {
            ciNames.push(mainCI);
        }
        
        // Add affected CIs
        const affectedCIs = getAffectedCIsList();
        ciNames.push(...affectedCIs);
        
        return ciNames;
    };

    // Get main CI name
    const getMainCI = () => {
        return changeData?.change_details?.cmdb_ci || null;
    };

    // Get affected CIs count (excluding main CI to avoid duplicates)
    const getAffectedCIsCount = () => {
        if (!changeData?.affected_cis?.length) return 0;
        
        const mainCI = changeData?.change_details?.cmdb_ci?.toUpperCase();
        const uniqueAffectedCIs = changeData.affected_cis.filter(ci => {
            const ciName = ci.ci_name?.toUpperCase();
            return ciName && ciName !== mainCI;
        });
        
        return uniqueAffectedCIs.length;
    };

    // Get list of unique affected CIs (excluding main CI)
    const getAffectedCIsList = () => {
        if (!changeData?.affected_cis?.length) return [];
        
        const mainCI = changeData?.change_details?.cmdb_ci;
        const uniqueCIs = new Set();
        
        changeData.affected_cis.forEach(ci => {
            const ciName = ci.ci_name;
            if (ciName && ciName !== mainCI) {
                uniqueCIs.add(ciName);
            }
        });
        
        return Array.from(uniqueCIs);
    };

    // Open add to list modal
    const handleOpenAddToList = async () => {
        setAddToListModalOpen(true);
    };

    if (loading) {
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    fontFamily: maerskFontFamily 
                }}
            >
                <McLoadingIndicator appearance="primary" />
            </Box>
        );
    }

    if (!changeData || !changeNumber) {
        return (
            <Box sx={{ padding: '24px', fontFamily: maerskFontFamily }}>
                <Alert severity="error">
                    Change details not found or invalid change number.
                </Alert>
            </Box>
        );
    }

    const details = changeData?.change_details;

    return (
        <div style={{
            fontFamily: maerskFontFamily,
            padding: '0 24px',
            paddingBottom: '50px',
            paddingTop: '10px',
        }}>
            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: { xs: 'flex-start', md: 'center' },
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                gap: { xs: 2, md: 0 },
                padding: '24px 0',
                marginLeft: '-24px',
                marginRight: '-24px',
                paddingLeft: '24px',
                paddingRight: '24px'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0, flex: 1, gap: 2 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography 
                                variant="h5" 
                                sx={{ 
                                    fontFamily: maerskFontFamily,
                                    color: '#333',
                                }}
                            >
                                {changeNumber}
                            </Typography>
                            {details.on_hold === "true" && (
                                <McTag
                                    label="On Hold"
                                    appearance="warning"
                                    fit="small"
                                />
                            )}
                            <McTag
                                label={details?.state || 'Unknown'}
                                appearance={getStateAppearance(details?.state)}
                                fit="small"
                            />
                            {details.risk && (
                                <McTag
                                    label={`${details.risk} Risk`}
                                    appearance={getRiskAppearance(details.risk)}
                                    fit="small"
                                />
                            )}
                            <McLinkButton
                                appearance="secondary"
                                href={`https://maersk.service-now.com/nav_to.do?uri=change_request.do?sysparm_query=number=${changeNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                fit="small"
                                variant="plain"
                                hiddenlabel
                                icon="square-arrow-up-right"
                            />
                        </Box>
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontFamily: maerskFontFamily, 
                                color: '#666',
                                wordBreak: 'break-word'
                            }}
                        >
                            {details?.short_description || 'Change Details'}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <McButton
                        appearance="secondary"
                        variant="outlined"
                        fit="small"
                        onClick={handleOpenAddToList}
                    >
                        <PlaylistAddIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Add to Subscription List
                    </McButton>
                </Box>
            </Box>

            {/* On Hold Alert */}
            {(details.on_hold === "true" && details.on_hold_reason) && (
                <Alert 
                    severity="warning"
                    sx={{ 
                        mb: 3, 
                        backgroundColor: '#FEF7E0',
                        border: '1px solid #F4D03F',
                        borderRadius: '8px'
                    }}
                >
                    <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, fontWeight: "600" }}>
                        Change On Hold
                    </Typography>
                    <Typography variant="body2" sx={{ 
                        fontFamily: maerskFontFamily,
                        mt: 1,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'pre-wrap'
                    }}>
                        {details.on_hold_reason}
                    </Typography>
                </Alert>
            )}

            <Box>
                {/* Tab Navigation */}
                <Box sx={{ mb: 3 }}>
                    <McTabBar 
                        tabchange={(e) => {
                            const tabs = ['overview', 'ai-analysis', 'dependencies'];
                            setSelectedTab(tabs[e.detail]);
                        }} 
                        currentindex={
                            selectedTab === 'overview' ? 0 : 
                            selectedTab === 'ai-analysis' ? 1 : 2
                        }
                    >
                        <McTab 
                            slot="tab" 
                            label="Overview" 
                            active={selectedTab === 'overview'}
                        >
                            <mc-icon slot="prefix" icon="chart-bars-vertical" size="16"></mc-icon>
                        </McTab>
                        <div slot="panel"></div>
                        <McTab 
                            slot="tab" 
                            label="AI Analysis" 
                            active={selectedTab === 'ai-analysis'}
                        >
                            <mc-icon slot="prefix" icon="sparkle" size="16"></mc-icon>
                        </McTab>
                        <div slot="panel"></div>
                        <McTab 
                            slot="tab" 
                            label="Affected Systems" 
                            active={selectedTab === 'dependencies'}
                        >
                            <mc-icon slot="prefix" icon="cog" size="16"></mc-icon>
                        </McTab>
                        <div slot="panel"></div>
                    </McTabBar>
                </Box>

                {/* Tab Content */}
                <Box>
                    {selectedTab === 'overview' && (
                        <OverviewTab 
                            changeData={changeData}
                            getStateAppearance={getStateAppearance}
                            getRiskAppearance={getRiskAppearance}
                            getTypeAppearance={getTypeAppearance}
                            getCategoryAppearance={getCategoryAppearance}
                        />
                    )}
                    {selectedTab === 'ai-analysis' && (
                        <AIAnalysisTab 
                            changeData={changeData}
                            changeNumber={changeNumber}
                            setisdrawerOpen={setisdrawerOpen}
                            setDrawerCi={setDrawerCi}
                            setDrawerCiCategory={setDrawerCiCategory}
                            setDrawerType={setDrawerType}
                        />
                    )}
                    {selectedTab === 'dependencies' && (
                        <DependenciesTab 
                            changeData={changeData}
                            getStateAppearance={getStateAppearance}
                            getRiskAppearance={getRiskAppearance}
                            getTypeAppearance={getTypeAppearance}
                            getCategoryAppearance={getCategoryAppearance}
                            setisdrawerOpen={setisdrawerOpen}
                            setDrawerCi={setDrawerCi}
                            setDrawerCiCategory={setDrawerCiCategory}
                            setDrawerType={setDrawerType}
                        />
                    )}
                </Box>
            </Box>
            
            {/* CI Info Drawer */}
            {isdrawerOpen && (
                <CiInfoDrawer
                    isdrawerOpen={isdrawerOpen}
                    setisdrawerOpen={setisdrawerOpen}
                    drawerCi={drawerCi}
                    drawerCiCategory={drawerCiCategory}
                    drawerType={drawerType}
                />
            )}

            {/* Add to Subscription List Modal */}
            <AddToSubscriptionListModal
                open={addToListModalOpen}
                onClose={() => setAddToListModalOpen(false)}
                graphData={graphData}
                applications={getAllCINames()}
                modalTitle="Add CIs to Subscription List"
                allowMultipleSelection={true}
                getApplicationsList={getAffectedCIsList}
                fontFamily={maerskFontFamily}
            />
        </div>
    );
};

export default ChangeDetailsPage;
